import type { PageRef, ComponentRef } from '@wix/editor-platform-sdk-types'
import type {
  FieldSpec,
  ObjectField,
  ReferenceField,
  Schema,
} from '@wix/wix-data-schema-types'
import type { DatasetType } from './datasetTypes'
import type { ReadWriteMode } from './readWriteModes'
import type { DataSettings, DataSettingsManager } from './wixDataSettings'
import type { BindingConfig } from './bindingConfig'

export * from './wixDataSettings'
export * from './datasetTypes'
export * from './readWriteModes'
export * from './bindingConfig'
export * from './cmsPublicAPI'

export interface ReferencedField {
  referencingFieldLabel: string
}
export interface NestedField {
  isNested: true
}

export type DigestedSimpleField = FieldSpec & {
  label: string
  name: string
  sortable: boolean
  isDeleted: boolean
  systemField: boolean
} & Partial<ReferencedField> &
  Partial<NestedField>

export type DigestedReferenceField = DigestedSimpleField &
  ReferenceField & {
    referencedCollectionDisplayField?: string
  }

export type DigestedObjectFieldWithFields = DigestedSimpleField &
  Required<ObjectField> & {
    isNested: never
  }

export type DigestedSchemaField =
  | DigestedSimpleField
  | DigestedReferenceField
  | DigestedObjectFieldWithFields

export enum AppCollectionVisibility {
  HIDDEN = 'hidden',
  VISIBLE = 'visible',
}

interface OpenConnectPanelArgs {
  componentRef: ComponentRef
  origin?: string
}

type ConvertStaticToDynamicOrigin = string

export type Translations = Record<string, string>

export type ValidationResultErrorParams = {
  max_length?: number
  conflicting_url?: string
}

export enum DynamicPageUrlValidationError {
  URL_TOO_LONG = 'URL_TOO_LONG',
  PREFIX_STARTS_WITH_TEXT = 'PREFIX_STARTS_WITH_TEXT',
  NO_FIELD_IN_PREFIX = 'NO_FIELD_IN_PREFIX',
  NO_SPECIAL_CHARS = 'NO_SPECIAL_CHARS',
  NO_DELETED_FIELD = 'NO_DELETED_FIELD',
  ONLY_EXISTING_FIELDS = 'ONLY_EXISTING_FIELDS',
  PATTERN_ALREADY_EXISTS = 'PATTERN_ALREADY_EXISTS',
  SEPARATE_FIELDS = 'SEPARATE_FIELDS',
  UNSUPPORTED_FIELDS = 'UNSUPPORTED_FIELDS',
  URL_EMPTY = 'URL_EMPTY',
  NO_VARIABLE = 'NO_VARIABLE',
  EXACT_PATTERN_EXISTS = 'EXACT_PATTERN_EXISTS',
}

export type ValidationResult =
  | { valid: true; invalidMessage?: string; warning?: string }
  | {
      valid: false
      invalidMessage?: string
      error: DynamicPageUrlValidationError | string
      params?: ValidationResultErrorParams
    }

export interface Sort {
  field: string
  order: 'asc' | 'desc'
}

export type FilterCondition =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'ge'
  | 'le'
  | 'lt'
  | 'contains'
  | 'notContains'
  | 'endsWith'
  | 'startsWith'
  | 'includesAny'
  | 'excludesAny'
  | 'includesAll'
  | 'excludesAll'
  | 'isEmpty'
  | 'isNotEmpty'

export interface Filter {
  condition: FilterCondition
  field: string
  path?: string
  value: string | string[] | number | null
  dataset?: ComponentRef
  datasetField?: string
  datasetFieldIsArray?: boolean
  component?: ComponentRef
  componentType?: string
  slug?: true
}

export interface DatasetConfig {
  collectionId: string
  includes: string[] | null
  readWriteMode: ReadWriteMode
  pageSize: number
  deferred: boolean
  filters: Filter[]
  sorts: Sort[]
  nested: null | unknown
  cursor: boolean
  private: boolean
  onlyUsedFields: boolean
  randomSort: boolean
}

export interface DatasetInfo {
  controllerRef: ComponentRef
  type: DatasetType
  displayName: string
  config: DatasetConfig
  isOnMasterPage: boolean
}

export interface CollectionsTransport {
  listSchemas: (options?: {
    includeDeletedCollections?: boolean
    includeHiddenAppCollections?: boolean
  }) => Promise<Schema[] | undefined>
  loadSchema: (collectionId: string) => Promise<Schema | undefined>
  saveSchema: (collectionId: string, schema: Schema) => Promise<void>
  removeSchema: (collectionId: string) => Promise<void>
  restoreSchema: (collectionId: string) => Promise<void>
}

// NOTE all functions params and results here should be serializable
export interface DatabindingPublicApi extends CollectionsTransport {
  addPresetById: (
    presetId: string,
    options: { origin: string },
  ) => Promise<{ pageRefs: PageRef[] }>

  setViewerStatusMessage: (viewerStatusMessage: string) => Promise<void>

  isDatasetUsingCursors: ({
    controllerRef,
  }: {
    controllerRef: ComponentRef
  }) => Promise<boolean>

  collectionsTransport: CollectionsTransport

  connectBindingSection: (params: {
    componentRef: ComponentRef
    controllerRef: ComponentRef
    newBindings: BindingConfig[]
    role: string
    isUserInputFilter?: boolean
    newColumns?: any[]
    requiresPrimaryConnection: boolean
  }) => Promise<void>

  createDataset: (
    pageRef: PageRef,
    dataset: {
      displayName: string
      datasetType?: DatasetType
      collectionId: string
      readWriteMode?: ReadWriteMode
      pageSize?: number
    },
  ) => Promise<DatasetInfo>

  getDataSettings: () => Promise<Required<DataSettings>>
  updateDataSettings: DataSettingsManager['updateDataSettings']
  /** @deprecated use getDataSettings instead https://jira.wixpress.com/browse/WD-7989 */
  getAppCollectionVisibility: () => Promise<AppCollectionVisibility>
  /** @deprecated use updateDataSettings instead https://jira.wixpress.com/browse/WD-7989 */
  setAppCollectionVisibility: (
    visibility: AppCollectionVisibility,
  ) => Promise<void>
  openConnectPanel: (args: OpenConnectPanelArgs) => Promise<void>
  convertStaticPageToDynamic: (params: {
    pageRef: PageRef
    origin: ConvertStaticToDynamicOrigin
  }) => Promise<unknown>
  addFieldToRoutingUrl: ({
    pageRef,
    field,
  }: {
    pageRef: PageRef
    field: string
  }) => Promise<{
    lowerCasedUrl: string
    routingUrlValidationResult: ValidationResult
  }>
  addFieldToRoutingUrlWithoutSave: ({
    pageRef,
    field,
  }: {
    pageRef: PageRef
    field: string
  }) => Promise<{
    lowerCasedUrl: string
    routingUrlValidationResult: ValidationResult
  }>
  getFieldsForDPUrl: ({
    pageRef,
  }: {
    pageRef: PageRef
  }) => Promise<DigestedSchemaField[]>
  getPageUrlAndValidationStatus: ({
    pageRef,
  }: {
    pageRef: PageRef
  }) => Promise<{
    dynamicPageRoutingUrl: string
    routingUrlValidationResult: ValidationResult
  }>
  getTranslations: (keys: string[]) => Promise<Translations>
  saveRoutingUrl: ({
    pageRef,
    newUrl,
  }: {
    pageRef: PageRef
    newUrl: string
  }) => Promise<{
    lowerCasedUrl: string
    routingUrlValidationResult: ValidationResult
  }>
  validateRoutingUrl: ({
    pageRef,
    routingUrl,
  }: {
    pageRef: PageRef
    routingUrl: string
  }) => Promise<ValidationResult>
}
